// Web Push client helpers. Subscriptions live in public.push_subscriptions;
// the sender is the push-notify Netlify function, triggered by a database
// webhook on every notifications insert.

import { supabase } from './supabase';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function pushDenied(): boolean {
  return pushSupported() && Notification.permission === 'denied';
}

/** iOS Safari only allows push for PWAs launched from the Home Screen. */
export function needsHomeScreenInstall(): boolean {
  const iOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  return iOS && !standalone;
}

export type Platform = 'mac' | 'windows' | 'iphone' | 'android' | 'other';

export function platformOf(userAgent: string): Platform {
  if (/iphone|ipad|ipod/i.test(userAgent)) return 'iphone';
  if (/android/i.test(userAgent)) return 'android';
  if (/macintosh|mac os x/i.test(userAgent)) return 'mac';
  if (/windows/i.test(userAgent)) return 'windows';
  return 'other';
}

/** The browser's app name as the OS lists it in notification settings. */
export function browserOf(userAgent: string): string {
  if (/edg\//i.test(userAgent)) return 'Microsoft Edge';
  if (/firefox\//i.test(userAgent)) return 'Firefox';
  if (/chrome\/|crios\//i.test(userAgent)) return 'Google Chrome';
  if (/safari\//i.test(userAgent)) return 'Safari';
  return 'your browser';
}

/** "Mac · Google Chrome", "iPhone · Cockpit app" — for the devices list. */
export function describeDevice(userAgent: string | null): string {
  if (!userAgent) return 'Unknown device';
  const platform = platformOf(userAgent);
  const name = { mac: 'Mac', windows: 'Windows PC', iphone: 'iPhone', android: 'Android', other: 'Device' }[platform];
  // iOS only pushes to the Home Screen app, so an iPhone subscription is always that.
  return platform === 'iphone' ? `${name} · Cockpit app` : `${name} · ${browserOf(userAgent)}`;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function getPushSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

/** Request permission, subscribe this browser, and store the subscription. */
export async function enablePush(userId: string): Promise<void> {
  if (!pushSupported()) throw new Error('Push is not supported in this browser');
  if (!VAPID_PUBLIC_KEY) throw new Error('VITE_VAPID_PUBLIC_KEY is not configured');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notification permission was not granted');

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
  });

  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('Browser returned an incomplete push subscription');
  }

  const { error } = await supabase.from('push_subscriptions').upsert(
    {
      user_id: userId,
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
      user_agent: navigator.userAgent.slice(0, 255),
    },
    { onConflict: 'endpoint' }
  );
  if (error) throw error;
}

/** Unsubscribe this browser and remove its stored subscription. */
export async function disablePush(): Promise<void> {
  const subscription = await getPushSubscription();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  const { error } = await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint);
  if (error) throw error;
}
